import React, { useState, useEffect } from 'react';
import { AdminService, EmailServiceStatusResponse } from '../../services/adminService';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useNotification } from '../../contexts/NotificationContext';

/** Callable responses may serialize Firestore Timestamps as seconds or _seconds */
function formatFirestoreTimestamp(value: unknown): string | null {
  if (value == null || typeof value !== 'object') return null;
  const o = value as Record<string, unknown>;
  const sec =
    typeof o.seconds === 'number'
      ? o.seconds
      : typeof o._seconds === 'number'
        ? o._seconds
        : null;
  if (sec != null) return new Date(sec * 1000).toLocaleString();
  return null;
}

const AdminEmailService: React.FC = () => {
  const { isDark } = useAdminTheme();
  const { showError, showSuccess } = useNotification();
  const [status, setStatus] = useState<EmailServiceStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastCheck, setLastCheck] = useState<Date | null>(null);
  const [sendingTest, setSendingTest] = useState<'ses' | 'zeptomail' | null>(null);

  useEffect(() => {
    checkStatus();
  }, []);

  const handleSendTestEmail = async (provider: 'ses' | 'zeptomail') => {
    try {
      setSendingTest(provider);
      await AdminService.sendTestEmail(provider);
      showSuccess(`Test email sent successfully via ${provider === 'ses' ? 'Amazon SES' : 'ZeptoMail'}`);
    } catch (error: any) {
      console.error(`Error sending test email via ${provider}:`, error);
      showError(error.message || `Failed to send test email via ${provider}`);
    } finally {
      setSendingTest(null);
    }
  };

  const checkStatus = async () => {
    try {
      setLoading(true);
      const data = await AdminService.getEmailServicesStatus();
      setStatus(data);
      setLastCheck(new Date());
    } catch (error) {
      console.error('Error checking email status:', error);
      showError('Failed to check email service status');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (statusStr: string) => {
    switch (statusStr) {
      case 'working': return 'text-green-600 dark:text-green-400';
      case 'error': return 'text-red-600 dark:text-red-400';
      case 'not_configured': return 'text-yellow-600 dark:text-yellow-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusBadge = (statusStr: string) => {
    const baseClasses = "px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch (statusStr) {
      case 'working': 
        return <span className={`${baseClasses} bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400`}>Working</span>;
      case 'error': 
        return <span className={`${baseClasses} bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400`}>Error</span>;
      case 'not_configured': 
        return <span className={`${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400`}>Not Configured</span>;
      default: 
        return <span className={`${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300`}>Unknown</span>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Email Service Status</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Monitor the health and connectivity of your email delivery providers.
          </p>
        </div>
        <button
          onClick={checkStatus}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {loading ? (
            <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
          Refresh Status
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Amazon SES Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2L1 21h22L12 2zm0 3.45L18.82 19H5.18L12 5.45z"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Amazon SES</h2>
                  <p className="text-sm text-gray-500">Marketing & Bulk Emails</p>
                </div>
              </div>
              {status ? getStatusBadge(status.ses.status) : <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-full"></div>}
            </div>

            <div className="mb-6 flex justify-end">
              <button
                onClick={() => handleSendTestEmail('ses')}
                disabled={sendingTest === 'ses' || status?.ses.status !== 'working'}
                className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                title={status?.ses.status !== 'working' ? 'SES is not currently working' : 'Send a manual test email'}
              >
                {sendingTest === 'ses' ? (
                  <svg className="animate-spin h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                )}
                {sendingTest === 'ses' ? 'Sending...' : 'Send test email'}
              </button>
            </div>

            {status?.ses.status === 'working' && status.ses.details ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Sent (Last 24h)</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{status.ses.details.sentLast24Hours}</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Max (24h Quota)</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{status.ses.details.max24HourSend}</p>
                  </div>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Max Send Rate</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{status.ses.details.maxSendRate} emails/sec</p>
                </div>
                <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Service is operational
                  </div>
                </div>
              </div>
            ) : status?.ses.status === 'error' ? (
              <div className="p-4 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-100 dark:border-red-900/20">
                <p className="text-sm text-red-700 dark:text-red-400 font-medium mb-1">Connection Failed</p>
                <p className="text-xs text-red-600 dark:text-red-300">{status.ses.error}</p>
                {!status.ses.configured && (
                  <p className="mt-2 text-xs text-red-800 dark:text-red-200 font-bold underline">
                    Configuration missing (AWS keys not found)
                  </p>
                )}
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center italic text-gray-400 text-sm">
                {loading ? 'Updating status...' : 'No status data available'}
              </div>
            )}
          </div>
        </div>

        {/* Zeptomail Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z"/>
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Zeptomail</h2>
                  <p className="text-sm text-gray-500">Transactional Emails</p>
                </div>
              </div>
              {status ? getStatusBadge(status.zeptomail.status) : <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-full"></div>}
            </div>

            <div className="mb-6 flex justify-end">
              <button
                onClick={() => handleSendTestEmail('zeptomail')}
                disabled={sendingTest === 'zeptomail' || status?.zeptomail.status !== 'working'}
                className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                title={status?.zeptomail.status !== 'working' ? 'Zeptomail is not currently working' : 'Send a manual test email'}
              >
                {sendingTest === 'zeptomail' ? (
                  <svg className="animate-spin h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                )}
                {sendingTest === 'zeptomail' ? 'Sending...' : 'Send test email'}
              </button>
            </div>

            {status?.zeptomail.status === 'working' ? (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-100 dark:border-blue-900/20">
                  <p className="text-sm text-blue-700 dark:text-blue-400 font-medium">Authentication Successful</p>
                  <p className="text-xs text-blue-600 dark:text-blue-300 mt-1">API Key is valid and reachable.</p>
                </div>
                <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Service is operational
                  </div>
                </div>
                <div className="text-xs text-gray-400 italic">
                  Note: Zeptomail is used as primary for verification and fallback for marketing.
                </div>
              </div>
            ) : status?.zeptomail.status === 'not_configured' ? (
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/10 rounded-lg border border-yellow-100 dark:border-yellow-900/20">
                <p className="text-sm text-yellow-700 dark:text-yellow-400 font-medium">Not Configured</p>
                <p className="text-xs text-yellow-600 dark:text-yellow-300">ZEPTOMAIL_API_KEY environment variable is missing.</p>
              </div>
            ) : status?.zeptomail.status === 'error' ? (
              <div className="p-4 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-100 dark:border-red-900/20">
                <p className="text-sm text-red-700 dark:text-red-400 font-medium mb-1">API Error {status.zeptomail.code && `(${status.zeptomail.code})`}</p>
                <p className="text-xs text-red-600 dark:text-red-300">{status.zeptomail.error}</p>
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center italic text-gray-400 text-sm">
                {loading ? 'Updating status...' : 'No status data available'}
              </div>
            )}
          </div>
        </div>
      </div>

      
      {/* Detailed Delivery Stats */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Detailed Delivery Stats</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-6 py-4 font-semibold">Provider</th>
                <th className="px-6 py-4 font-semibold text-center">Sent Today</th>
                <th className="px-6 py-4 font-semibold text-center">Total Sent</th>
                <th className="px-6 py-4 font-semibold text-right">Last Email Sent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center text-orange-600">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2L1 21h22L12 2zm0 3.45L18.82 19H5.18L12 5.45z"/>
                      </svg>
                    </div>
                    <span className="font-medium text-gray-900 dark:text-white">Amazon SES</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-center font-bold text-gray-900 dark:text-white">
                  {status?.ses.stats?.sentToday.toLocaleString() ?? '0'}
                </td>
                <td className="px-6 py-4 text-center text-gray-600 dark:text-gray-400">
                  {status?.ses.stats?.totalSent.toLocaleString() ?? '0'}
                </td>
                <td className="px-6 py-4 text-right text-gray-500 dark:text-gray-400 text-sm">
                  {formatFirestoreTimestamp(status?.ses.stats?.lastSentAt) ?? 'Never'}
                </td>
              </tr>
              <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center text-blue-600">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z"/>
                      </svg>
                    </div>
                    <span className="font-medium text-gray-900 dark:text-white">Zeptomail</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-center font-bold text-gray-900 dark:text-white">
                  {status?.zeptomail.stats?.sentToday.toLocaleString() ?? '0'}
                </td>
                <td className="px-6 py-4 text-center text-gray-600 dark:text-gray-400">
                  {status?.zeptomail.stats?.totalSent.toLocaleString() ?? '0'}
                </td>
                <td className="px-6 py-4 text-right text-gray-500 dark:text-gray-400 text-sm">
                  {formatFirestoreTimestamp(status?.zeptomail.stats?.lastSentAt) ?? 'Never'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-center text-xs text-gray-400">
        {lastCheck && `Last checked: ${lastCheck.toLocaleString()}`}
      </p>
    </div>
  );
};

export default AdminEmailService;
